import React from "react"
import { useRender } from "@base-ui/react/use-render"
import { useNavigationContext } from "./navigation-context"
import { matchesPath } from "./navigation-utils"
import { NavigationButton } from "./navigation-button"

// =============================================================================
// Props
// =============================================================================

export type NavigationGroupItemProps = {
  label: string
  /**
   * Pathname prefix to match against `Navigation`'s `currentPath`. The item
   * is active when `currentPath.startsWith(pathPrefix)`.
   */
  pathPrefix?: string
  tooltip?: string
  render?: useRender.RenderProp
}

// =============================================================================
// Sub-components
// =============================================================================

function Dot() {
  return (
    <span aria-hidden className="isolate flex size-6 shrink-0 items-center justify-center">
      <span className="size-[7px] rounded-full bg-gray-300 transition-colors group-data-active:bg-gray-900" />
    </span>
  )
}

// =============================================================================
// Component
// =============================================================================

/**
 * A child entry rendered inside a `Navigation.Group`.
 * Must be placed directly inside `Navigation.Group`.
 *
 * Renders a label with a dot on the left. The parent group draws the vertical
 * line connecting siblings; the dot belongs to this component and changes
 * colour when the item is active.
 *
 * Pass a `render` prop to inject your own link component (e.g. Inertia `Link`)
 * so the DS doesn't need to know about routing.
 *
 * @example
 * <Navigation.Group icon="bolt" label="Automation">
 *   <Navigation.GroupItem label="Sequences" render={<Link href="/sequences" />} />
 *   <Navigation.GroupItem label="Rules" render={<Link href="/rules" />} />
 * </Navigation.Group>
 */
export function NavigationGroupItem({ label, pathPrefix, tooltip, render }: NavigationGroupItemProps) {
  const { currentPath } = useNavigationContext()
  const isActive = matchesPath(currentPath, pathPrefix)

  return (
    <li data-slot="navigation-group-item">
      <NavigationButton label={label} isActive={isActive} tooltip={tooltip} render={render}>
        <Dot />
        <span className="truncate text-left font-medium">{label}</span>
      </NavigationButton>
    </li>
  )
}

NavigationGroupItem.displayName = "Navigation.GroupItem"
